// import node module libraries
import { useState, useEffect, use } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Col, Row, Modal, Card, Container, Form, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// import custom components
import { FormSelect } from 'components/elements/form-select/FormSelect';

// import axios
import axios from 'axios';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const CheckoutForm = ({ customerData, customerTaxData, onSubmit, onCancel, isLoading }) => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [startTime, setStartTime] = useState(Date.now()); // Set start time for bot detection

	const [formData, setFormData] = useState({
		company: customerData?.name || '',
		line1: customerData?.address?.line1 || '',
		line2: customerData?.address?.line2 || '',
		postal_code: customerData?.address?.postal_code || '',
		city: customerData?.address?.city || '',
		country: customerData?.address?.country || '',
		email: customerData?.email || ''
	});

	const vatCodes = [
		{
			country: 'DE',
			value: 'eu_vat',
			label: <span>🇩🇪 {t('DE VAT')}</span>,
			placeholder: 'DE123456789',
			pattern: /^DE\d{9}$/,
			errorMessage: t('Invalid German VAT format. Use: DE123456789')
		},
		{
			country: 'AT',
			value: 'eu_vat',
			label: <span>🇦🇹 {t('AT VAT')}</span>,
			placeholder: 'ATU12345678',
			pattern: /^ATU\d{8}$/,
			errorMessage: t('Invalid Austrian VAT format. Use: ATU12345678')
		},
		{
			country: 'CH',
			value: 'eu_vat',
			label: <span>🇨🇭 {t('CH VAT')}</span>,
			placeholder: 'CHE-123.456.789 MWST',
			pattern: /^CHE-\d{3}\.\d{3}\.\d{3}\s(MWST|TVA|IVA)$/,
			errorMessage: t('Invalid Swiss VAT format. Use: CHE-123.456.789 MWST')
		}
	];

	const [formTaxData, setFormTaxData] = useState(
		customerTaxData && customerTaxData.data && customerTaxData.data.length > 0
			? customerTaxData.data.map(tax => {
				// Hier müssen wir den richtigen tax_id_type basierend auf dem Land setzen
				const vatCode = vatCodes.find(code => code.country === tax.country);
				return {
					id: tax.id || '',
					tax_id_country: tax.country || '',
					tax_id_type: vatCode ? vatCode.value : (tax.type || ''), // Setze den Wert basierend auf vatCodes
					tax_id_value: tax.value || ''
				};
			})
			: [{ id: '', tax_id_country: '', tax_id_type: '', tax_id_value: '' }]
	);

	{
		formTaxData.map((taxData, index) => (
			<div key={index}>
				{/* DEBUG: Zeige die aktuellen Werte an */}
				<div style={{ backgroundColor: '#f0f0f0', padding: '10px', marginBottom: '10px' }}>
					<small>
						DEBUG - tax_id_country: {taxData.tax_id_country},
						tax_id_type: {taxData.tax_id_type},
						tax_id_value: {taxData.tax_id_value}
					</small>
				</div>
				<Row>
					{/* ...existing code... */}
				</Row>
			</div>
		))
	}

	// Funktion zum Hinzufügen eines neuen VAT-Felds --> deaktiviert, da es nur voerst ein Feld geben soll
	// const addVatField = () => {
	// 	setFormTaxData([...formTaxData, { id: '', tax_id_type: '', tax_id_value: '' }]);
	// };

	// Funktion zum Entfernen eines VAT-Felds
	const removeVatField = (index) => {
		if (formTaxData.length > 1) {
			const updatedTaxData = formTaxData.filter((_, i) => i !== index);
			setFormTaxData(updatedTaxData);
		}
	};
/*
	// Funktion zum Aktualisieren eines bestimmten VAT-Felds
	const updateVatField = (index, field, value) => {
		const updatedTaxData = formTaxData.map((tax, i) =>
			i === index ? { ...tax, [field]: value } : tax
		);
		setFormTaxData(updatedTaxData);
	};*/

	useEffect(() => {
		setStartTime(Date.now());
	}, []);

	const countryCodes = [
		{ value: t('DE'), label: <span>🇩🇪 {t('Deutschland')}</span> },
		{ value: t('AT'), label: <span>🇦🇹 {t('Österreich')}</span> },
		{ value: t('CH'), label: <span>🇨🇭 {t('Schweiz')}</span> }
	];

	// State für VAT-Validierung
	const [vatErrors, setVatErrors] = useState({});

	// Funktion zur VAT-Validierung
	const validateVatNumber = (vatCountry, vatValue) => {
		const vatCode = vatCodes.find(code => code.country === vatCountry); // Suche nach country statt key
		if (!vatCode || !vatValue) return null;

		return vatCode.pattern.test(vatValue) ? null : vatCode.errorMessage;
	};

	// Funktion für multiple Field-Updates
	const updateVatFieldMultiple = (index, updates) => {
		const updatedTaxData = formTaxData.map((tax, i) =>
			i === index ? { ...tax, ...updates } : tax
		);

		setFormTaxData(updatedTaxData);

		// Validierung wenn nötig
		if (updates.tax_id_value || updates.tax_id_country) {
			const currentTaxData = updatedTaxData[index];
			const error = validateVatNumber(
				updates.tax_id_country || currentTaxData.tax_id_country,
				updates.tax_id_value || currentTaxData.tax_id_value
			);
			setVatErrors(prev => ({
				...prev,
				[index]: error
			}));
		}
	};

	// Behalten Sie die ursprüngliche updateVatField für einzelne Updates
	const updateVatField = (index, field, value) => {
		const updatedTaxData = formTaxData.map((tax, i) =>
			i === index ? { ...tax, [field]: value } : tax
		);

		setFormTaxData(updatedTaxData);

		// Validierung bei VAT-Wert Änderung
		if (field === 'tax_id_value') {
			const currentTaxData = updatedTaxData[index];
			const error = validateVatNumber(currentTaxData.tax_id_country, value);
			setVatErrors(prev => ({
				...prev,
				[index]: error
			}));
		}

		// Validierung auch bei VAT-Typ Änderung (eigentlich Country-Änderung)
		if (field === 'tax_id_country') {
			const currentTaxData = updatedTaxData[index];
			const error = validateVatNumber(value, currentTaxData.tax_id_value);
			setVatErrors(prev => ({
				...prev,
				[index]: error
			}));
		}
	};

	return (
		<div className="">
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<p className="mb-4">{t('Please fill in your invoice details to complete the checkout.')}</p>	
			{/* form section */}
			<div id="form" className="">
				<Form onSubmit={(e) => {
					e.preventDefault();
					onSubmit(formData, formTaxData);
				}}>
					<Row>
						{/* Company */}
						<Col md={12} sm={12}>
							<Form.Group className="mb-3" controlId="company">
								<Form.Label>
									{t('Company Name')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="text"
									name="company"
									placeholder={t('Enter Company Name')}
									value={formData.company || ''}
									autoComplete="billing name"
									onChange={(e) => setFormData({ ...formData, company: e.target.value })}
									required
								/>
							</Form.Group>
						</Col>
						{/* Address */}
						<Col md={12} sm={12}>
							<Form.Group className="mb-3" controlId="address">
								<Form.Label>
									{t('Address')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="text"
									name="line1"
									placeholder={t('Enter Address Line 1')}
									value={formData.line1 || ''}
									autoComplete="billing address-line1"
									onChange={(e) => setFormData({ ...formData, line1: e.target.value })}
									//required
								/>
								<Form.Control
									className="mt-3"
									type="text"
									name="line2"
									placeholder={t('Enter Address Line 2')}
									value={formData.line2 || ''}
									onChange={(e) => setFormData({ ...formData, line2: e.target.value })}
									autoComplete="billing address-line2"
								/>
							</Form.Group>
						</Col>
						{/* ZIP Code */}
						<Col md={6} sm={12}>
							<Form.Group className="mb-3" controlId="postal_code">
								<Form.Control
									type="text"
									name="postal_code"
									placeholder={t('Enter Postal Code')}
									value={formData.postal_code || ''}
									onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
									autoComplete="billing zip"
									//required
								/>
							</Form.Group>
						</Col>
						{/* City */}
						<Col md={6} sm={12}>
							<Form.Group className="mb-3" controlId="city">
								<Form.Control
									type="text"
									name="city"
									placeholder={t('Enter City')}
									value={formData.city || ''}
									onChange={(e) => setFormData({ ...formData, city: e.target.value })}
									autoComplete="billing city"
									//required
								/>
							</Form.Group>
						</Col>
						{/* Country */}
						<Col md={12} sm={12}>
							<Form.Group className="mb-3" controlId="country">
								<Form.Label>
									{t('Country')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Select
									name="country"
									value={formData.country || ''}
									onChange={(e) => {
										const selectedCountry = e.target.value;
										setFormData({
											...formData,
											country: selectedCountry,
											// Optionally reset tax_id_type when country changes
											tax_id_type: '',
										});
									}}
									autoComplete="billing country"
									//required
								>
									<option value="">{t('Select Country')}</option>
									{countryCodes.map((country, index) => (
										<option key={index} value={country.value}>
											{country.label}
										</option>
									))}
								</Form.Select>
							</Form.Group>
						</Col>
						{/* Email */}
						<Col md={12} sm={12}>
							<Form.Group className="mb-3" controlId="email">
								<Form.Label>
									{t('Email')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="email"
									name="email"
									placeholder={t('Enter Email')}
									value={formData.email || ''}
									onChange={(e) => setFormData({ ...formData, email: e.target.value })}
									autoComplete="email"
									required />
							</Form.Group>
						</Col>
						{/* VAT Fields - Dynamic */}
						<Form.Label>
							{t('VAT-ID')} <span className="text-danger"></span>
						</Form.Label>
						{formTaxData.map((taxData, index) => (
							<div key={index}>
								<Row>
									<Col md={6} sm={6}>
										<Form.Group className="mb-2" controlId={`tax_id_type_${index}`}>
											<Form.Select
												name={`tax_id_type_${index}`}
												onChange={(e) => {
													const selectedCountry = e.target.value;
													const selectedVatCode = vatCodes.find(code => code.country === selectedCountry);
													if (selectedVatCode) {
														// Beide Updates in einem Aufruf
														updateVatFieldMultiple(index, {
															'tax_id_type': selectedVatCode.value,
															'tax_id_country': selectedCountry
														});
													} else {
														updateVatFieldMultiple(index, {
															'tax_id_type': '',
															'tax_id_country': selectedCountry
														});
													}
												}}
												value={taxData.tax_id_country || ''}
												autoComplete="billing vat"
											>
												<option value="">{t('Select VAT Type')}</option>
												{vatCodes.map((code, codeIndex) => (
													<option key={codeIndex} value={code.country}>
														{code.label}
													</option>
												))}
											</Form.Select>
										</Form.Group>
									</Col>
									<Col md={6} sm={6}>
										<Form.Group className="mb-2" controlId={`tax_id_value_${index}`}>
											<Form.Control
												type="text"
												name={`tax_id_value_${index}`}
												onChange={(e) => updateVatField(index, 'tax_id_value', e.target.value)}
												placeholder={
													vatCodes.find(code => code.country === taxData.tax_id_country)?.placeholder ||
													t('Enter VAT Number')
												}
												value={taxData.tax_id_value || ''}
												autoComplete="billing vat"
												isInvalid={vatErrors[index]}
											/>
											{vatErrors[index] && (
												<Form.Control.Feedback type="invalid">
													{vatErrors[index]}
												</Form.Control.Feedback>
											)}
										</Form.Group>
									</Col>
									<Col md={2} sm={2} className="d-flex align-items-end">
										<Form.Group className="mb-3">
											{formTaxData.length > 1 && (
												<Button
													variant="outline-danger"
													size="sm"
													className="rounded"
													onClick={() => removeVatField(index)}
												>
													{t('Remove')}
												</Button>
											)}
										</Form.Group>
									</Col>
									<Col md={12} sm={12} className="d-flex align-items-end">
										<Form.Group className="mt-2">
											{/* {index === formTaxData.length - 1 && (
											<Button
												variant="outline-primary"
												size="sm"
												className="rounded"
												onClick={addVatField}
											>
												{t('Add VAT')}
											</Button>
										)} */}
										</Form.Group>
									</Col>
								</Row>
							</div>
						))}
						{/* button */}
						<Modal.Footer>
							<Button variant="secondary" onClick={onCancel}>
								{t('Cancel')}
							</Button>
							<Button variant="primary" type="submit" disabled={isLoading}>
								{isLoading ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" />
										{t('Save')}
									</>
								) : (
									t('Save')
								)}
							</Button>
						</Modal.Footer>
					</Row>
				</Form>
			</div>
		</div>
	);
};
export default CheckoutForm;